import React, { useState } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  Typography, 
  Container, 
  Box, 
  Tabs, 
  Tab,
  Paper,
  Grid,
  Chip,
  Alert
} from '@mui/material';
import { 
  BillEventType, 
  BillStatus, 
  Bill,
  PaymentTransaction,
  PaymentTransactionType 
} from '@/types';
import { billEventService } from '@/services/billEventService';
import BillTimeline from '@/components/bill/BillTimeline';
import PaymentTransactionManager from '@/components/bill/PaymentTransactionManager';

/**
 * Enhanced demo to showcase both bill timeline and payment transaction management
 */
const PaymentTransactionDemo: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);

  // Create comprehensive sample data for demo
  const samplePaymentTransactions: PaymentTransaction[] = [
    {
      id: 'txn_001',
      type: PaymentTransactionType.RECEIVED,
      amount: 150.00,
      personId: 'person_001',
      personName: 'Anna Schmidt',
      reference: 'REF-2024-001',
      paymentMethod: 'Banküberweisung',
      description: 'Erste Teilzahlung für Skipass',
      timestamp: new Date('2024-08-19T19:09:00'),
      billEventId: 'event_002',
      createdBy: 'user456',
      createdByName: 'Admin User',
      createdAt: new Date('2024-08-19T19:09:00'),
      updatedAt: new Date('2024-08-19T19:09:00'),
    },
    {
      id: 'txn_002',
      type: PaymentTransactionType.RECEIVED,
      amount: 200.00,
      personId: 'person_001',
      personName: 'Anna Schmidt',
      reference: 'REF-2024-002',
      paymentMethod: 'Banküberweisung',
      description: 'Zweite Teilzahlung',
      timestamp: new Date('2024-08-20T19:09:00'),
      billEventId: 'event_003',
      createdBy: 'user456',
      createdByName: 'Admin User',
      createdAt: new Date('2024-08-20T19:09:00'),
      updatedAt: new Date('2024-08-20T19:09:00'),
    },
    {
      id: 'txn_003',
      type: PaymentTransactionType.RECEIVED,
      amount: 150.00,
      personId: 'person_002',
      personName: 'Max Mustermann',
      reference: 'REF-2024-003',
      paymentMethod: 'PayPal',
      description: 'Finale Zahlung',
      timestamp: new Date('2024-08-21T19:09:00'),
      billEventId: 'event_004',
      createdBy: 'user456',
      createdByName: 'Admin User',
      createdAt: new Date('2024-08-21T19:09:00'),
      updatedAt: new Date('2024-08-21T19:09:00'),
    },
    {
      id: 'txn_004',
      type: PaymentTransactionType.SENT,
      amount: 25.00,
      personId: 'person_003',
      personName: 'Hotel Alpenblick',
      reference: 'REFUND-001',
      paymentMethod: 'Überweisung',
      description: 'Rückerstattung für Stornierung',
      timestamp: new Date('2024-08-22T10:30:00'),
      billEventId: 'event_005',
      createdBy: 'user456',
      createdByName: 'Admin User',
      createdAt: new Date('2024-08-22T10:30:00'),
      updatedAt: new Date('2024-08-22T10:30:00'),
    }
  ];

  // Create sample events for timeline
  const sampleEvents = [
    billEventService.createBillCreatedEvent(
      'user123',
      'Max Mustermann'
    ),
    
    billEventService.createBillSentEvent(
      'user123',
      'Max Mustermann',
      ['anna.schmidt@example.com', 'max.mustermann@example.com'],
      'E-Mail'
    ),
    
    billEventService.createPaymentReceivedEvent(
      150.00,
      150.00,
      350.00,
      'user456',
      'Anna Schmidt',
      'Banküberweisung',
      'REF-2024-001'
    ),
    
    billEventService.createPaymentReceivedEvent(
      200.00,
      350.00,
      150.00,
      'user456',
      'Anna Schmidt',
      'Banküberweisung',
      'REF-2024-002'
    ),
    
    billEventService.createPaymentReceivedEvent(
      150.00,
      500.00,
      0.00,
      'user456',
      'Max Mustermann',
      'PayPal',
      'REF-2024-003'
    ),

    billEventService.createPaymentSentEvent(
      25.00,
      'user456',
      'Admin User',
      'Hotel Alpenblick',
      'Überweisung',
      'REFUND-001'
    ),
    
    billEventService.createFullyPaidEvent(
      500.00,
      'user456',
      'Max Mustermann',
      150.00
    )
  ];

  // Adjust timestamps for better demo display
  sampleEvents[0].timestamp = new Date('2024-08-17T19:09:00');
  sampleEvents[1].timestamp = new Date('2024-08-18T19:09:00');
  sampleEvents[2].timestamp = new Date('2024-08-19T19:09:00');
  sampleEvents[3].timestamp = new Date('2024-08-20T19:09:00');
  sampleEvents[4].timestamp = new Date('2024-08-21T19:09:00');
  sampleEvents[5].timestamp = new Date('2024-08-22T10:30:00');
  sampleEvents[6].timestamp = new Date('2024-08-21T19:09:00');

  // Sample bill data
  const sampleBill: Partial<Bill> = {
    id: 'demo-123',
    apartmentName: 'Apartment Sunside',
    grandTotal: 500.00,
    paidAmount: 475.00, // 500 received - 25 sent
    status: BillStatus.FULLY_PAID,
    events: sampleEvents,
    paymentTransactions: samplePaymentTransactions,
    generatedAt: new Date('2024-08-17T19:09:00'),
  };

  // Mock handlers for demo
  const handleTransactionUpdate = () => {
    console.log('Transaction update triggered - this is a demo');
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Payment Transaction Management Demo
      </Typography>
      
      <Typography variant="body1" color="text.secondary" paragraph>
        Diese Demo zeigt das neue Zahlungsmanagement-System für individuelle Transaktionen.
        Es ermöglicht die detaillierte Verfolgung von Zahlungseingängen und -ausgängen mit
        Personenzuordnung und Referenzen für die Kontoauszug-Zuordnung.
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Demo-Features:</strong> Zahlungseingang/ausgang hinzufügen, 
          Personenzuordnung, Referenzverfolgung, Timeline-Integration, 
          Automatische Statusaktualisierung
        </Typography>
      </Alert>

      <Grid container spacing={3}>
        {/* Bill Summary Card */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardHeader
              title="Demo Rechnung"
              subheader="Apartment Sunside - Demo Bill"
            />
            <CardContent>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Rechnung ID: {sampleBill.id}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Erstellt: {sampleBill.generatedAt?.toLocaleDateString('de-DE')}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Gesamtbetrag:</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    €{sampleBill.grandTotal?.toFixed(2)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Eingänge:</Typography>
                  <Typography variant="body2" color="success.main">
                    €{samplePaymentTransactions
                      .filter(t => t.type === PaymentTransactionType.RECEIVED)
                      .reduce((sum, t) => sum + t.amount, 0)
                      .toFixed(2)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Ausgänge:</Typography>
                  <Typography variant="body2" color="error.main">
                    €{samplePaymentTransactions
                      .filter(t => t.type === PaymentTransactionType.SENT)
                      .reduce((sum, t) => sum + t.amount, 0)
                      .toFixed(2)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Saldo:</Typography>
                  <Typography variant="body2" color="success.main" fontWeight="bold">
                    €{sampleBill.paidAmount?.toFixed(2)}
                  </Typography>
                </Box>
                <Box sx={{ mt: 1 }}>
                  <Chip 
                    label="Vollständig Bezahlt" 
                    color="success" 
                    size="small"
                    variant="outlined"
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Main Demo Content */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
                <Tab label="Payment Transactions" />
                <Tab label="Event Timeline" />
                <Tab label="Implementation Notes" />
              </Tabs>

              {tabValue === 0 && (
                <PaymentTransactionManager
                  tripId="demo-trip"
                  billId="demo-123"
                  transactions={samplePaymentTransactions}
                  billGrandTotal={sampleBill.grandTotal || 0}
                  currentPaidAmount={sampleBill.paidAmount || 0}
                  onUpdate={handleTransactionUpdate}
                />
              )}

              {tabValue === 1 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Event Timeline
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Chronologische Darstellung aller Ereignisse mit automatischer 
                    Integration der Zahlungstransaktionen.
                  </Typography>
                  <BillTimeline events={sampleEvents} showDetails={true} />
                </Box>
              )}

              {tabValue === 2 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Implementation Features
                  </Typography>
                  
                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                    ✅ Payment Transaction Management:
                  </Typography>
                  <ul>
                    <li>Individual transaction tracking (incoming/outgoing)</li>
                    <li>Person assignment for bank statement reconciliation</li>
                    <li>Reference numbers for transaction matching</li>
                    <li>Payment method selection</li>
                    <li>Optional descriptions for additional context</li>
                  </ul>

                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                    ✅ UI Components:
                  </Typography>
                  <ul>
                    <li>PaymentTransactionDialog for adding transactions</li>
                    <li>PaymentTransactionManager for overview and management</li>
                    <li>Enhanced BillCard with tabs</li>
                    <li>German localization throughout</li>
                  </ul>

                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                    ✅ Integration:
                  </Typography>
                  <ul>
                    <li>Automatic bill event creation for timeline</li>
                    <li>Real-time bill status updates</li>
                    <li>Firebase Firestore persistence</li>
                    <li>TypeScript type safety</li>
                  </ul>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default PaymentTransactionDemo;